import { useState, useMemo } from 'react';
import { Box, TextField, Tabs, Tab, Typography } from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import GeolocationCard from './GeolocationCard';
import { usePolygonTypeList } from '../../../stores/polygonTypeStore';
import { useLanguage } from '../../../utils/i18n';

const norm = (s) => (s || '').toString().trim().toLowerCase();

const isVehicleInLocation = (vehicle = {}, loc = {}, polygonTypeData = []) => {
  if (!vehicle || !loc) return false;

  const statusRaw = String(vehicle.status || vehicle.position || vehicle.location || '').toLowerCase();
  const spatialTypeRaw = String(vehicle?.spatial_type || vehicle?.raw?.spatial_type || '').toLowerCase();
  const spatialDescRaw = String(vehicle?.raw?.spatial_description || vehicle?.spatial_description || '').toLowerCase();

  const isDelay = statusRaw.includes('delay');
  const isAtPool = statusRaw.includes('at pool') || (isDelay && spatialTypeRaw.includes('pool'));
  const isAtCustomer = statusRaw.includes('at customer') || (isDelay && spatialTypeRaw.includes('customer'));
  const isAtGeofence = statusRaw.includes('at geofence') || (isDelay && spatialTypeRaw.includes('geofence'));

  const locType = norm(loc.location_type);

  let typeMatches = false;
  if (locType === 'pit' && isAtPool) typeMatches = true;
  else if (locType === 'jetty' && isAtCustomer) typeMatches = true;
  else if (locType === 'geofence' && isAtGeofence) {
    if (polygonTypeData && Array.isArray(polygonTypeData)) {
      for (const pt of polygonTypeData) {
        if (spatialDescRaw.includes(norm(pt.name))) {
          typeMatches = true;
          break;
        }
      }
    }
  }

  if (!typeMatches) return false;

  const locNamesToMatch = [
    norm(loc.name),
    norm(loc.pool_code),
    norm(loc.geofence_name),
    norm(loc.transit_point_name),
    norm(loc.join?.pool_name),
    norm(loc.join?.area_name)
  ].filter(Boolean);

  const vehicleFields = [norm(vehicle.location), norm(vehicle.spatial_description)].filter(Boolean);

  return locNamesToMatch.some((p) => vehicleFields.some((vf) => vf === p));
};

const extractCoords = (p = {}) => {
  if (!p) return null;
  if (p.centroid_lat != null && p.centroid_lon != null) {
    return { lat: Number(p.centroid_lat), lon: Number(p.centroid_lon) };
  }
  if (p.latitude != null && p.longitude != null) {
    return { lat: Number(p.latitude), lon: Number(p.longitude) };
  }
  if (p.coords && p.coords.lat != null && (p.coords.lng != null || p.coords.lon != null)) {
    return { lat: Number(p.coords.lat), lon: Number(p.coords.lng ?? p.coords.lon) };
  }
  try {
    const feats = p.info?.features;
    if (Array.isArray(feats) && feats.length && feats[0].geometry?.coordinates) {
      const coords = feats[0].geometry.coordinates[0];
      if (Array.isArray(coords) && coords.length) {
        const first = coords[0]; // [lon, lat]
        if (Array.isArray(first) && first.length >= 2) {
          return { lat: Number(first[1]), lon: Number(first[0]) };
        }
      }
    }
  } catch (err) {
    console.log(err);
  }
  return null;
};

export default function GeolocationData({
  preFilteredLocations = [], // Data yang sudah di filter company oleh MapContent
  vehicleList = [],
  onSelectLocation = () => {},
  onSelectVehicle = () => {}
}) {
  const { t } = useLanguage();
  const [openId, setOpenId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('All');

  const { data: polygonTypeData } = usePolygonTypeList(0, 1000, 'name', '');

  const filterTabs = useMemo(() => {
    const list = [
      { label: 'All', value: 'All' },
      { label: 'PIT', value: 'PIT' },
      { label: 'Jetty', value: 'Jetty' }
    ];
    if (polygonTypeData && Array.isArray(polygonTypeData)) {
      polygonTypeData.forEach((pt) => {
        const l = norm(pt.name);
        if (l !== 'pit' && l !== 'jetty') {
          list.push({ label: pt.name.charAt(0).toUpperCase() + pt.name.slice(1), value: pt.name });
        }
      });
    }
    return list;
  }, [polygonTypeData]);

  // Menghitung kendaraan yang berada di masing-masing lokasi
  const mappedLocations = useMemo(() => {
    return preFilteredLocations.map((loc) => {
      const vehiclesInLoc = (vehicleList || []).filter((veh) => isVehicleInLocation(veh, loc, polygonTypeData));

      return {
        ...loc,
        unique_ui_id: `${loc.location_type}-${loc.id}`,
        total_vehicle: vehiclesInLoc.length,
        vehicles_raw: vehiclesInLoc
      };
    });
  }, [preFilteredLocations, vehicleList, polygonTypeData]);

  // Melakukan filter lanjutan (Tab tipe lokasi dan Text search)
  const filteredLocations = useMemo(() => {
    let result = mappedLocations;

    // 1. FILTER TIPE LOKASI
    if (filterType !== 'All') {
      if (norm(filterType) === 'pit' || norm(filterType) === 'jetty') {
        result = result.filter((loc) => norm(loc.location_type) === norm(filterType));
      } else {
        result = result.filter((loc) => {
          if (norm(loc.location_type) !== 'geofence') return false;
          const ptName = norm(loc.polygon_type_name || loc.join?.polygon_type?.name || loc.join?.polygon_type_name);
          if (ptName && ptName.includes(norm(filterType))) return true;
          const ptData = polygonTypeData?.find((p) => norm(p.name) === norm(filterType));
          if (ptData && (String(loc.polygon_type) === String(ptData.id) || String(loc.polygon_type_id) === String(ptData.id))) return true;
          return false;
        });
      }
    }

    // 2. FILTER PENCARIAN TEKS
    if (searchQuery) {
      const q = norm(searchQuery);
      result = result.filter((loc) => {
        const name = norm(loc.name || loc.pool_name || loc.geofence_name || loc.transit_point_name);
        return name.includes(q);
      });
    }

    return result;
  }, [mappedLocations, filterType, searchQuery, polygonTypeData]);

  const handleToggle = (uniqueId) => {
    setOpenId(openId === uniqueId ? null : uniqueId);
  };

  const handleClickHeader = (loc) => {
    const coords = extractCoords(loc);
    if (coords) onSelectLocation({ ...coords, raw: loc });
    else onSelectLocation(loc);

    setOpenId(loc.unique_ui_id);
  };

  const handleFilterChange = (event, newType) => {
    if (newType !== null) {
      setFilterType(newType);
      setOpenId(null);
    }
  };

  return (
    <Box sx={{ width: 1, mt: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ pb: 1, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
          <TextField
            label={t('mapsTracking.searchLocation', 'Cari Lokasi...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            fullWidth
            variant="outlined"
            sx={{ bgcolor: '#fff', borderRadius: 1 }}
          />
        </Box>

        {/* =================================================== */}

        <Tabs
          value={filterType}
          onChange={handleFilterChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{
            style: { backgroundColor: '#ffffff', height: 3, borderRadius: '3px 3px 0 0' }
          }}
          sx={{
            minHeight: 36,
            mb: 1,
            bgcolor: '#485D9A',
            borderRadius: 1,
            border: '1px solid rgba(0, 0, 0, 0.12)',
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
            '& .MuiTabs-scrollButtons': {
              color: '#ffffff',
              opacity: 1,
              width: 28,
              '&.Mui-disabled': {
                opacity: 0.3
              }
            },
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.5,
              px: 2,
              textTransform: 'none',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.85rem',
              fontWeight: 'bold',
              minWidth: 'auto',
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                color: '#ffffff',
                bgcolor: 'rgba(255, 255, 255, 0.15)'
              },
              '&:hover': {
                color: '#ffffff',
                bgcolor: 'rgba(255, 255, 255, 0.08)'
              }
            }
          }}
        >
          {filterTabs.map((t) => (
            <Tab key={t.value} label={t.label} value={t.value} disableRipple />
          ))}
        </Tabs>
      </Box>

      <Box
        sx={{
          pb: 12,
          flexGrow: 1,
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(72, 93, 154, 0.3) transparent',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(72, 93, 154, 0.3)',
            borderRadius: '10px',
            transition: 'background-color 0.3s ease'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(72, 93, 154, 0.6)'
          }
        }}
      >
        {filteredLocations.map((item) => (
          <GeolocationCard
            key={item.unique_ui_id}
            item={item}
            isOpen={openId === item.unique_ui_id}
            onToggle={() => handleToggle(item.unique_ui_id)}
            onClickHeader={handleClickHeader}
            onClickVehicle={(veh) => onSelectVehicle(veh)}
          />
        ))}

        {filteredLocations.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '250px',
              p: 3,
              textAlign: 'center',
              bgcolor: 'rgba(72, 93, 154, 0.03)',
              borderRadius: 1,
              border: '1px dashed rgba(72, 93, 154, 0.25)',
              transition: 'all 0.3s ease'
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2.5,
                boxShadow: '0px 8px 24px rgba(72, 93, 154, 0.12)'
              }}
            >
              <SearchOffIcon sx={{ fontSize: 36, color: '#485D9A', opacity: 0.8 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ color: '#485D9A', fontWeight: 700, mb: 1 }}>
              {t('mapsTracking.locationNotFound', 'Lokasi Tidak Ditemukan')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              {t('mapsTracking.noLocationMatchKeyword', 'Maaf, tidak ada lokasi yang cocok dengan kata kunci "')}
              <b>{searchQuery}</b>
              {t('mapsTracking.orCurrentFilter', '" atau filter saat ini.')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
